import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Eye, EyeOff, UserRound } from 'lucide-react';
import { DashboardLayout } from '../components/layout';
import { adminApi } from '../api/examApi';
import { toast } from 'sonner';
import { parseApiError } from '../lib/apiErrors';
import {
  CLASS_LEVELS, BOARDS, COMPETITIVE_EXAMS, SUBJECT_COMBOS, useSubjectOptions, subjectIdsFor,
  SelectField, TextField, SectionHeading, SubjectMultiSelect,
} from '../components/student/StudentProfileFields';

const EMPTY_FORM = {
  name: '',
  email: '',
  password: '',
  phone_number: '',
  main_contact_number: '',
  class_level: '',
  board: '',
  board_other: '',
  competitive_exam: '',
  father_name: '',
  father_mobile: '',
  father_email: '',
  mother_name: '',
  mother_mobile: '',
  mother_email: '',
};

export default function AdminCreateStudentPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(EMPTY_FORM);
  const subjectOptions = useSubjectOptions();
  const [subjects, setSubjects] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  const toggleSubject = (subject) => {
    setSubjects((prev) => (
      prev.includes(subject) ? prev.filter((s) => s !== subject) : [...prev, subject]
    ));
  };

  const applyCombo = (combo) => {
    setSubjects((prev) => {
      const current = new Set(prev);
      const allSelected = combo.subjects.every((s) => current.has(s));
      if (allSelected) {
        combo.subjects.forEach((s) => current.delete(s));
      } else {
        combo.subjects.forEach((s) => current.add(s));
      }
      return [...current];
    });
  };

  const isComboActive = (combo) => combo.subjects.every((s) => subjects.includes(s));

  const validate = () => {
    const next = {};
    if (!formData.name.trim() || formData.name.trim().length < 2) {
      next.name = 'Name must be at least 2 characters';
    }
    if (!formData.email.trim()) {
      next.email = 'Email is required';
    }
    if (!formData.password || formData.password.length < 6) {
      next.password = 'Password must be at least 6 characters';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const subjectIds = subjectIdsFor(subjects, subjectOptions.options);
      if (subjectIds === null) {
        toast.error('Subject list is not loaded yet or is out of date. Reload the page and try again.');
        setSaving(false);
        return;
      }
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        password: formData.password,
        phone_number: formData.phone_number || null,
        main_contact_number: formData.main_contact_number || null,
        class_level: formData.class_level ? parseInt(formData.class_level, 10) : null,
        board: formData.board || null,
        board_other: formData.board === 'OTHER' ? (formData.board_other || null) : null,
        competitive_exam: formData.competitive_exam || null,
        father_name: formData.father_name || null,
        father_mobile: formData.father_mobile || null,
        father_email: formData.father_email || null,
        mother_name: formData.mother_name || null,
        mother_mobile: formData.mother_mobile || null,
        mother_email: formData.mother_email || null,
        subject_ids: subjectIds,
      };
      const { data } = await adminApi.createStudent(payload);
      toast.success('Student account created');
      navigate(`/admin/students/${data.id}`);
    } catch (err) {
      if (err.response?.status === 409) {
        setErrors((prev) => ({ ...prev, email: 'This email is already registered' }));
      } else {
        const { fieldErrors, message } = parseApiError(err, 'Failed to create student');
        setErrors((prev) => ({ ...prev, ...fieldErrors }));
        toast.error(message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout title="Create Student Account">
      <div className="animate-fade-in max-w-4xl space-y-6">
        <button
          onClick={() => navigate('/admin/students')}
          className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-accent-cool transition-colors"
        >
          <ArrowLeft size={18} /> Back to Student Directory
        </button>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary dark:bg-accent-cool/10 dark:text-accent-cool">
                <UserRound size={26} />
              </div>
              <div>
                <h2 className="text-2xl font-heading font-bold text-slate-900 dark:text-white">
                  New Student Account
                </h2>
                <p className="text-slate-500 dark:text-slate-400">
                  Create login credentials and profile details on the student's behalf.
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
            <SectionHeading>Credentials</SectionHeading>
            <div className="grid gap-6 md:grid-cols-2">
              <div>
                <TextField label="Full Name" name="name" value={formData.name} onChange={handleChange} />
                {errors.name && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.name}</p>}
              </div>
              <div>
                <TextField label="Email" name="email" value={formData.email} onChange={handleChange} type="email" />
                {errors.email && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email}</p>}
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 pr-10 text-slate-900 focus:ring-2 focus:ring-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {errors.password && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.password}</p>}
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
            <SectionHeading>Personal Information</SectionHeading>
            <div className="grid gap-6 md:grid-cols-2">
              <TextField label="Student Contact" name="phone_number" value={formData.phone_number} error={errors.phone_number} onChange={handleChange} type="tel" />
              <TextField label="Main Contact Number" name="main_contact_number" value={formData.main_contact_number} error={errors.main_contact_number} onChange={handleChange} type="tel" />
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
            <SectionHeading>Academic Profile</SectionHeading>
            <div className="grid gap-6 md:grid-cols-2">
              <SelectField
                label="Class"
                name="class_level"
                value={formData.class_level}
                error={errors.class_level}
                onChange={handleChange}
                options={CLASS_LEVELS.map((n) => ({ value: String(n), label: `Class ${n}` }))}
              />
              <SelectField
                label="Board"
                name="board"
                value={formData.board}
                error={errors.board}
                onChange={handleChange}
                options={BOARDS}
              />
              {formData.board === 'OTHER' && (
                <div className="md:col-span-2">
                  <TextField
                    label="Specify Board"
                    name="board_other"
                    value={formData.board_other}
                    error={errors.board_other}
                    onChange={handleChange}
                    placeholder="Enter board name"
                  />
                </div>
              )}
              <SelectField
                label="Competitive Exam Target"
                name="competitive_exam"
                value={formData.competitive_exam}
                error={errors.competitive_exam}
                onChange={handleChange}
                options={COMPETITIVE_EXAMS}
              />
            </div>

            <div className="mt-6">
              <label className="mb-3 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Subjects
              </label>
              <div className="mb-3 flex flex-wrap gap-2">
                {SUBJECT_COMBOS.map((combo) => (
                  <button
                    key={combo.label}
                    type="button"
                    onClick={() => applyCombo(combo)}
                    className={[
                      'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                      isComboActive(combo)
                        ? 'border-primary bg-primary text-white dark:border-accent-cool dark:bg-accent-cool'
                        : 'border-slate-300 bg-white text-slate-600 hover:border-primary hover:text-primary dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300 dark:hover:border-accent-cool dark:hover:text-accent-cool',
                    ].join(' ')}
                  >
                    {combo.label}
                  </button>
                ))}
              </div>
              <SubjectMultiSelect
                options={subjectOptions.names}
                selected={subjects}
                onToggle={toggleSubject}
                loading={!subjectOptions.loaded}
              />
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
            <SectionHeading>Parent / Guardian Information</SectionHeading>
            <div className="grid gap-8 md:grid-cols-2">
              <div className="space-y-4">
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">Father</p>
                <TextField label="Father's Name" name="father_name" value={formData.father_name} error={errors.father_name} onChange={handleChange} />
                <TextField label="Father's Mobile" name="father_mobile" value={formData.father_mobile} error={errors.father_mobile} onChange={handleChange} type="tel" />
                <TextField label="Father's Email" name="father_email" value={formData.father_email} error={errors.father_email} onChange={handleChange} type="email" />
              </div>
              <div className="space-y-4">
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">Mother</p>
                <TextField label="Mother's Name" name="mother_name" value={formData.mother_name} error={errors.mother_name} onChange={handleChange} />
                <TextField label="Mother's Mobile" name="mother_mobile" value={formData.mother_mobile} error={errors.mother_mobile} onChange={handleChange} type="tel" />
                <TextField label="Mother's Email" name="mother_email" value={formData.mother_email} error={errors.mother_email} onChange={handleChange} type="email" />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate('/admin/students')}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-md border border-slate-300 dark:border-slate-600 px-5 py-2.5 font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 font-medium text-white hover:bg-primary-light disabled:opacity-50"
            >
              <Save size={18} />
              {saving ? 'Creating…' : 'Create Student'}
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
